import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import logoImage from "../images/logo.png";
import { trackEvent } from "../helpers/trackEvent";
import AuthSuccessPanel from "./AuthSuccessPanel";
import { useAuth } from "./AuthContext";
import { isEventbriteEnabled } from "../eventbrite/eventbriteFeature";
import "./Auth.css";

const pageContent = {
  login: {
    title: "Sign in to BadgeFlow",
    description: "Access your badge editor workspace and continue preparing attendee badges.",
    submitLabel: "Sign in",
    alternatePrompt: "New to BadgeFlow?",
    alternateLabel: "Create an account",
    alternatePath: "/signup",
  },
  signup: {
    title: "Create your account",
    description: "Use Supabase authentication to keep your BadgeFlow workspace secure.",
    submitLabel: "Create account",
    alternatePrompt: "Already have an account?",
    alternateLabel: "Sign in",
    alternatePath: "/login",
  },
};

const SIGNUP_PASSWORD_MIN_LENGTH = 8;

const AuthPage = ({ mode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authError, clearAuthError, isAuthenticated, isConfigured, signIn, signInWithGoogle, signInWithEventbrite, signUp } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formError, setFormError] = useState("");
  const [notice, setNotice] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const content = pageContent[mode];
  const hasSignupNotice = mode === "signup" && Boolean(notice);
  const previousModeRef = useRef(mode);

  useEffect(() => {
    if (previousModeRef.current === mode) return;

    previousModeRef.current = mode;
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setNotice("");
    setFormError("");
    setIsSubmitting(false);
    clearAuthError();
  }, [clearAuthError, mode]);

  const redirectTo = useMemo(() => {
    const from = location.state?.from?.pathname;
    return from && from !== "/login" && from !== "/signup" ? from : "/";
  }, [location.state]);

  if (isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    clearAuthError();
    setFormError("");
    setNotice("");

    if (mode === "signup") {
      if (password.length < SIGNUP_PASSWORD_MIN_LENGTH) {
        setFormError("Password must be at least 8 characters.");
        return;
      }

      if (password !== confirmPassword) {
        setFormError("Passwords do not match.");
        return;
      }
    }

    setIsSubmitting(true);

    try {
      if (mode === "login") {
        await signIn({ email, password });
        trackEvent({
          action: "sign_in",
          data: {
            method: "email",
          },
        });
        navigate(redirectTo, { replace: true });
      } else {
        await signUp({ email, password });
        trackEvent({
          action: "sign_up",
          data: {
            method: "email",
          },
        });
        setNotice("Activation link sent");
      }
    } catch (error) {
      setFormError(error.message || "Authentication failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    clearAuthError();
    setFormError("");
    setNotice("");
    setIsSubmitting(true);

    try {
      await signInWithGoogle();
      trackEvent({
        action: mode === "signup" ? "sign_up" : "sign_in",
        data: {
          method: "google",
        },
      });
    } catch (error) {
      setFormError(error.message || "Google sign-in failed. Please try again.");
      setIsSubmitting(false);
    }
  };

  const handleEventbriteSignIn = async () => {
    clearAuthError();
    setFormError("");
    setNotice("");
    setIsSubmitting(true);
    try {
      await signInWithEventbrite();
      trackEvent({ action: mode === "signup" ? "sign_up" : "sign_in", data: { method: "eventbrite" } });
    } catch (error) {
      setFormError(error.message || "Eventbrite sign-in failed. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <main className="auth-shell">
      <section className="auth-card">
        <Link className="auth-logo" to="/">
          <img src={logoImage} alt="BadgeFlow logo" />
        </Link>
        <h1>{content.title}</h1>

        {!hasSignupNotice && (authError || formError) && (
          <div className="auth-alert" role="alert">
            {formError || authError}
          </div>
        )}

        {hasSignupNotice ? (
          <AuthSuccessPanel
            actionLabel="Go to sign in"
            actionTo="/login"
            title={notice}
          >
            <p>
              We sent a secure activation link to <strong>{email}</strong>.
              Confirm your account there, then come back to sign in.
            </p>
          </AuthSuccessPanel>
        ) : (
          <>
            <button
              className="auth-google-button"
              disabled={isSubmitting || !isConfigured}
              onClick={handleGoogleSignIn}
              type="button"
            >
              <span className="auth-google-icon" aria-hidden="true">G</span>
              Continue with Google
            </button>

            {isEventbriteEnabled() && <button
              className="auth-google-button auth-eventbrite-button"
              disabled={isSubmitting || !isConfigured}
              onClick={handleEventbriteSignIn}
              type="button"
            >
              <span className="auth-google-icon auth-eventbrite-icon" aria-hidden="true">e</span>
              Sign in with Eventbrite
            </button>}

            <div className="auth-divider"><span>or continue with email</span></div>

            {/* <p className="auth-description">Use your email and password to sign in</p> */}

            <form className="auth-form" onSubmit={handleSubmit}>
              <label htmlFor="email">Email</label>
              <input
                id="email"
                autoComplete="email"
                inputMode="email"
                onChange={(event) => setEmail(event.target.value)}
                required
                type="email"
                value={email}
              />

              <label htmlFor="password">Password</label>
              <input
                id="password"
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                minLength={mode === "signup" ? SIGNUP_PASSWORD_MIN_LENGTH : 6}
                onChange={(event) => setPassword(event.target.value)}
                required
                type="password"
                value={password}
              />

              {mode === "signup" && (
                <>
                  <label htmlFor="confirm-password">Confirm password</label>
                  <input
                    id="confirm-password"
                    autoComplete="new-password"
                    minLength={SIGNUP_PASSWORD_MIN_LENGTH}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    required
                    type="password"
                    value={confirmPassword}
                  />
                </>
              )}

              {mode === "login" && (
                <Link className="auth-secondary-link" to="/forgot-password">
                  Forgot your password?
                </Link>
              )}

              <button className="primary auth-submit" disabled={isSubmitting || !isConfigured} type="submit">
                {isSubmitting ? "Please wait…" : content.submitLabel}
              </button>
            </form>

            {mode === "signup" && (
              <p className="auth-legal">
                By creating an account, you agree to our{" "}
                <a href="https://badgeflow.app/terms" rel="noreferrer" target="_blank">
                  Terms of Service
                </a>{" "}
                and acknowledge our{" "}
                <a href="https://badgeflow.app/privacy" rel="noreferrer" target="_blank">
                  Privacy Policy
                </a>.
              </p>
            )}

            <p className="auth-switch">
              {content.alternatePrompt} <Link to={content.alternatePath}>{content.alternateLabel}</Link>
            </p>
          </>
        )}
      </section>
      {mode === "login" && !hasSignupNotice && <DemoWidget />}
    </main>
  );
};

const DemoWidget = () => (
  <div className="auth-demo-widget">
    Need a quick preview? Try the{" "}
    <Link
      to="/demo"
      onClick={() => {
        trackEvent({
          action: "demo_mode_clicked",
        });
      }}
    >
      live demo
    </Link>
  </div>
);

export default AuthPage;

